import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Escape user-controlled values for safe interpolation into HTML strings (XSS protection). */
export function escapeHtml(value: unknown): string {
  const s = value == null ? "" : String(value);
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function safeText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => safeText(item)).filter(Boolean).join(" ");
  }
  return "";
}

export function safeLower(value: unknown): string {
  return safeText(value).toLowerCase();
}

/** Capitaliza a primeira letra de cada palavra: "joão silva" → "João Silva" */
export function capitalizeName(value: string): string {
  return value
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (char) => char.toUpperCase());
}

/** Format a categoria value (string | string[]) into a readable label e.g. "Iniciante, Avançado" */
export function formatCategoria(cat: unknown): string {
  if (!cat) return "—";
  const arr = Array.isArray(cat) ? cat : [cat];
  return arr
    .map((c: unknown) => {
      const s = String(c || "").trim();
      return s.charAt(0).toUpperCase() + s.slice(1);
    })
    .filter(Boolean)
    .join(", ");
}

/** Get the first category key (lowercase) for config lookup */
export function getCategoriaKey(cat: unknown): string {
  if (!cat) return "";
  const arr = Array.isArray(cat) ? cat : [cat];
  return String(arr[0] || "").toLowerCase().trim();
}

/** Check if a turma's categoria (string | string[]) contains a specific category */
export function turmaMatchesCategory(turma: { categoria?: unknown }, cat: string): boolean {
  const arr = Array.isArray(turma.categoria) ? turma.categoria : [turma.categoria];
  return arr.some((c: unknown) => String(c || "").toLowerCase().trim() === cat);
}

/** Extract all unique individual categories from turmas array */
export function extractUniqueCategories(turmas: { categoria?: unknown }[]): string[] {
  const set = new Set<string>();
  turmas.forEach((t) => {
    const arr = Array.isArray(t.categoria) ? t.categoria : [t.categoria];
    arr.forEach((c: unknown) => { const s = String(c || "").toLowerCase().trim(); if (s) set.add(s); });
  });
  return Array.from(set).sort();
}
